import { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { loadCurrentEmail } from "@/office/integration";

export interface CurrentEmail {
  subject: string;
  from: string;
  to: string;
  body: string;
}

/**
 * Shows the currently-open email (subject + from + collapsible body) with a
 * "Load current email" button that reads it via the Office body API. For the
 * text-based features (translate / tone / proofread) an optional "into text
 * field" button copies the body into the feature's input.
 */
export function CurrentEmailPreview({
  email,
  onLoaded,
  busy,
  onUseInText,
}: {
  email: CurrentEmail | null;
  onLoaded: (e: CurrentEmail) => void;
  busy: boolean;
  onUseInText?: (body: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleLoad() {
    setLoading(true);
    try {
      const e = await loadCurrentEmail();
      onLoaded(e);
      if (e.subject || e.body) setExpanded(true);
    } finally {
      setLoading(false);
    }
  }

  if (!email) {
    return (
      <Button size="small" variant="outlined" onClick={handleLoad} disabled={loading || busy}>
        {loading ? "Reading…" : "Load current email"}
      </Button>
    );
  }

  return (
    <Box
      sx={{
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 1,
        p: 1,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" fontWeight="bold" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {email.subject || "(no subject)"}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={handleLoad} disabled={loading} title="Reload current email">
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setExpanded((x) => !x)} title="Toggle preview">
            <ExpandMoreIcon
              fontSize="small"
              sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 150ms" }}
            />
          </IconButton>
        </Stack>
      </Stack>

      {email.from && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          from {email.from}
        </Typography>
      )}

      <Collapse in={expanded}>
        <Box
          sx={{
            mt: 0.5,
            maxHeight: 160,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            fontSize: 12,
            color: "text.secondary",
            bgcolor: "background.default",
            p: 0.5,
            borderRadius: 0.5,
          }}
        >
          {email.body || "(empty body)"}
        </Box>
        {onUseInText && (
          <Button
            size="small"
            onClick={() => onUseInText(email.body)}
            sx={{ mt: 0.5 }}
          >
            Into text field ↓
          </Button>
        )}
      </Collapse>
    </Box>
  );
}
